import React, { CSSProperties, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { localize } from "src/l10n";
import { ITimelineContext, TimelineProps, TimelineState, TimelineStateProps } from "./Timeline.types";
import { useDispatch, useSelector } from "react-redux";
import { plannerItemUpdated, queryPlannerBoards, queryPlannerCategories, queryPlannerItems, queryPlannerLabels } from "src/redux";
import { SpintrTypes } from "src/typings";
import { updatePlannerCategoryAsync, updatePlannerCategoryTypeAsync, updatePlannerItemAsync } from "src/api/plannerApi";
import { UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import TimelineColumn from "src/components/Planner/TimelineColumn/TimelineColumn";
import { PlannerTimelineGroup, PlannerTimelineCategory, PlannerBarGroup, PlannerTimelineItem, ColumnDuration, RailClickHandler, UpdateCategoryHandler, UpdateCategoryTypeHandler, PlannerPositioning, IPlannerBoardSetting } from "src/components/Planner/types";
import { assignLayersToItems, generateColumnDurations, getTimelineDuration, plannerStyling } from "src/components/Planner/utils";
import TimelineBoards from "src/components/Planner/TimelineBoards/TimelineBoards";
import TimelineScroller from "src/components/Planner/TimelineScroller/TimelineScroller";
import api from "src/spintr/SpintrApi";
import { AxiosResponse } from "axios";

export const TimelineContext = React.createContext<ITimelineContext>(null);

function Timeline({
    onBarClick,
    onCreateClick,
    timelineMode,
    searchText,
    displayBoardForm,
    rights,
    boardId
}: TimelineProps): ReactElement {
    const dispatch = useDispatch();
    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const stateProps = useSelector<Spintr.AppState, TimelineStateProps>(
        (appState) => ({
            isSmallViewMode: appState.ui.isSmallViewMode,
            itemLookup: appState.planner.itemsById,
            queryItemIds: appState.planner.queryItemIds,
            boardLookup: appState.planner.boardsById,
            labelLookup: appState.planner.labelsById,
            user: appState.profile.active,
            appMode: appState.ui.appMode,
            initialItemsFetchDone: appState.planner.initialItemsFetchDone,
            initialBoardsFetchDone: appState.planner.initialBoardsFetchDone,
        }),
        (left, right) => {
            if (left.user.id !== right.user.id) {
                return false;
            }

            if (left.queryItemIds !== right.queryItemIds) {
                return false;
            }

            if (left.itemLookup !== right.itemLookup) {
                return false;
            }

            if (left.boardLookup !== right.boardLookup) {
                return false;
            }

            if (left.labelLookup !== right.labelLookup) {
                return false;
            }

            if (left.initialItemsFetchDone !== right.initialItemsFetchDone) {
                return false;
            }

            if (left.initialBoardsFetchDone !== right.initialBoardsFetchDone) {
                return false;
            }

            return true;
        }
    );

    const [state, setState] = useState<TimelineState>(() => {
        const now = new Date();
        now.setHours(12, 0, 0, 0);

        return {
            todayMs: now.getTime(),
            initialTopMostItemIndex: 0,
            headerHeight: 0,
            timelineBoardsHeight: 0,
            axisMinimized: false
        };
    });

    const [data, setData] = useState<PlannerTimelineGroup[]>([]);
    const [settings, setSettings] = useState<IPlannerBoardSetting[]>([]);

    useEffect(() => {
        const text = (searchText || "").toLowerCase();

        const items = Object.values(stateProps.itemLookup)
            .filter((item) => item.contentStatus === SpintrTypes.ContentStatus.Published)
            .filter((item) =>
                text.length === 0 ||
                item.name.toLowerCase().includes(text) ||
                (item?.category?.name && item.category.name.toLowerCase().includes(text))
            );

        const boards = !!boardId ?
            [stateProps.boardLookup[boardId]].filter(Boolean) :
            Object.values(stateProps.boardLookup).filter(x => !x.isHidden);

        const tree = boards
            .sort((a, b) => {
                const aSetting = settings.find(bs => bs.type === a.type);
                const bSetting = settings.find(bs => bs.type === b.type);

                const aIndex = aSetting ? aSetting.index : Number.MAX_SAFE_INTEGER;
                const bIndex = bSetting ? bSetting.index : Number.MAX_SAFE_INTEGER;

                return aIndex - bIndex;
            }).map(board => ({
                ...board,
                categories: Object.values(
                    items
                        .filter(item => ((!!item.boardId && item.boardId === board.id) || (!item.boardId && board.type === 1)))
                        .reduce((acc, item) => {
                            const key = item.category.id + "+" + item.categoryType?.id;
                            if (!acc[key]) {
                                acc[key] = {
                                    id: key,
                                    boardId: item.boardId,
                                    categoryId: item.category.id,
                                    categoryTypeId: item.categoryType?.id,
                                    name: item.category.name,
                                    categoryTypeName: item.categoryType?.name,
                                    items: [],
                                    isReadOnly: item.category.isReadOnly,
                                    isLangTag: item.category.isLangTag,
                                };
                            }

                            acc[key].items.push(item);
                            return acc;
                        }, {})
                )
            })).sort((a, b) => a.index - b.index);

        for (const board of tree) {
            if (board.canEdit) {
                board.categories.push({
                    id: "",
                    boardId: board.id,
                    categoryId: "",
                    categoryTypeId: "",
                    name: "",
                    categoryTypeName: "",
                    items: [],
                    isReadOnly: false,
                    isPlaceholder: true
                });
            }
        }

        setData((previousBoards) => tree.map((board) => {
            const categories: PlannerTimelineCategory[] = board.categories.map((category: Spintr.PlannerItemCategory) => {
                const categoryItems = items.filter((item) =>
                    item.category.id === category.categoryId &&
                    item.categoryType?.id === category.categoryTypeId &&
                    ((!!item.boardId && item.boardId === board.id) || (!item.boardId && board.type === 1))
                );

                if (categoryItems.length === 0 && !category.isPlaceholder) {
                    return null;
                }

                return {
                    key: category.id,
                    boardId: category.boardId,
                    categoryId: category.categoryId,
                    categoryTypeId: category.categoryTypeId,
                    name: category.isLangTag
                        ? localize(category.name)
                        : category.name,
                    categoryTypeName: category.categoryTypeName,
                    contentStatus: category.contentStatus,
                    order: category.order,
                    isReadOnly: category.isReadOnly,
                    isPlaceholder: category.isPlaceholder,
                    start: categoryItems.reduce(
                        (min, item) =>
                            min < item.startsAt ? min : item.startsAt,
                        new Date(Number.MAX_SAFE_INTEGER)
                    ),
                    end: categoryItems.reduce(
                        (max, item) =>
                            max > item.endsAt ? max : item.endsAt,
                        new Date(Number.MIN_SAFE_INTEGER)
                    ),
                    items: assignLayersToItems(
                        categoryItems.map((item) => ({
                            key: item.id,
                            name: item.name,
                            start: item.allDayEvent
                                ? new Date(
                                    item.startsAt.getFullYear(),
                                    item.startsAt.getMonth(),
                                    item.startsAt.getDate(),
                                    0,
                                    0,
                                    0,
                                    0
                                )
                                : item.startsAt,
                            end: item.allDayEvent
                                ? new Date(
                                    item.endsAt.getFullYear(),
                                    item.endsAt.getMonth(),
                                    item.endsAt.getDate(),
                                    23,
                                    59,
                                    59,
                                    999
                                )
                                : item.endsAt,
                            canEdit: board.canEdit && !category.isReadOnly,
                            categoryId: item.category.id,
                            boardId: item.boardId,
                        }))
                    ).map((barGroup) => {
                        const expanded = barGroup.layers > 1 &&
                            previousBoards.some((prevBoard) =>
                                prevBoard.categories.some((prevCategory) =>
                                    prevCategory.items.some(
                                        (prevBarGroup) =>
                                            prevBarGroup.key === barGroup.key &&
                                            prevBarGroup.expanded
                                    )
                                )
                            );

                        return {
                            ...barGroup,
                            expanded: expanded,
                            items: barGroup.items.map((item) => ({ ...item, expanded }))
                        }
                    }),
                };
            })
                .filter(Boolean)
                .sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    if (a.order !== undefined) return -1;
                    if (b.order !== undefined) return 1;
                    return a.start.getTime() - b.start.getTime();
                });

            return {
                key: board.id,
                name: board.name,
                expanded: previousBoards.length === 0 || previousBoards.some((prevBoard) => prevBoard.key === board.id && prevBoard.expanded),
                categories,
                type: board.type,
                color: board.color,
                canEdit: board.canEdit
            }
        }));
    }, [searchText, stateProps.itemLookup, stateProps.boardLookup, stateProps.user, setData, settings, boardId]);

    const onGroupClicked = useCallback(
        (group: PlannerTimelineGroup) => setData(
            (previousData) => previousData.map((g) => g.key === group.key
                ? { ...g, expanded: !g.expanded }
                : g,
            ),
        ),
        [setData],
    );

    const onBarGroupClicked = useCallback((barGroup: PlannerBarGroup) => {
        setData((previousData) => previousData.map((group) => ({
            ...group,
            categories: group.categories.map((category) => ({
                ...category,
                items: category.items.map((item) => {
                    const expanded = item.key === barGroup.key && !item.expanded;

                    return {
                        ...item,
                        expanded,
                        items: item.items.map((i) => ({ ...i, expanded }))
                    }
                }),
            })),
        })));
    }, []);

    const onItemDurationChange = useCallback(
        async (item: PlannerTimelineItem, startTime: number, endTime: number) => {
            const id = typeof item.key === "number" ? item.key : parseInt(item.key, 10);
            const plannerItem = stateProps.itemLookup[id];
            if (!plannerItem) {
                return;
            }

            const prevStart = plannerItem.startsAt;
            const prevEnd = plannerItem.endsAt;

            dispatch(plannerItemUpdated({
                ...plannerItem,
                startsAt: new Date(startTime),
                endsAt: new Date(endTime),
            }));

            try {
                const updatedItem = await updatePlannerItemAsync(plannerItem.id, {
                    ...plannerItem,
                    startsAt: new Date(startTime),
                    endsAt: new Date(endTime),
                    approvedBy: plannerItem.approvedBy?.id,
                    assignees: (plannerItem.assignees || []).map(
                        (item) => item.id,
                    ),
                    targetedTo: (plannerItem.targetedTo || []).map(
                        (item) => item.id,
                    ),
                    files: undefined,
                    participants: plannerItem.itemType === SpintrTypes.PlannerItemType.Event
                        ? (plannerItem.participants || []).map((p) => p.user?.id)
                        : undefined,
                    links: plannerItem.links || [],
                });

                dispatch(plannerItemUpdated({
                    ...updatedItem,
                    startsAt: new Date(updatedItem.startsAt),
                    endsAt: new Date(updatedItem.endsAt),
                }));
            } catch (reason) {
                console.error("Failed to update planner item", reason);

                dispatch(plannerItemUpdated({
                    ...plannerItem,
                    startsAt: prevStart,
                    endsAt: prevEnd,
                }));
            }
        },
        [dispatch, stateProps.itemLookup],
    );

    const { todayMs } = state;

    const columnDurations = useMemo<ColumnDuration[]>(
        () => generateColumnDurations(new Date(), timelineMode),
        [timelineMode]
    );

    const timelineDuration: ColumnDuration = useMemo(
        () => getTimelineDuration(columnDurations),
        [columnDurations]
    );

    const columnWidth = useMemo(() => {
        switch (timelineMode) {
            case "DAYS":
                return 200;
            case "WEEKS":
                return 62;
            default:
                return 350;
        }
    },
        [stateProps.isSmallViewMode, timelineMode]);

    const timelineWidth = useMemo<number>(
        () => columnWidth * columnDurations.length,
        [columnDurations.length, columnWidth]
    );

    const getIndexFromDate = useCallback((date: Date) => {
        const time = date.getTime();

        const closest = columnDurations.reduce((a, b) => {
            const midA = (a.startMilliseconds + a.endMilliseconds) / 2;
            const midB = (b.startMilliseconds + b.endMilliseconds) / 2;

            return Math.abs(midA - time) < Math.abs(midB - time) ? a : b;
        });

        return columnDurations.indexOf(closest);
    }, [columnDurations]);

    const onAxisToggleClicked = useCallback(() => {
        setState((prevState) => ({
            ...prevState,
            axisMinimized: !prevState.axisMinimized
        }));
    }, [setState, stateProps.isSmallViewMode]);

    const onRailClicked = useCallback<RailClickHandler>((boardId, categoryId, categoryTypeId, startTime, endTime) => {
        onCreateClick(boardId, categoryId, categoryTypeId, startTime, endTime);
    }, [onCreateClick]);

    const toggleBoardExpanded = useCallback((id: number) => setData((categories) => categories.map(
        (c) => c.key === id
            ? { ...c, expanded: !c.expanded }
            : c
    )), [setData]);

    const onCategoryUpdated = useCallback<UpdateCategoryHandler>(
        async (model) => {
            let previousCategory = null;

            setData((prevData) => prevData.map(
                (group) => group.categories.every((category) => category.categoryId !== model.id)
                    ? group
                    : ({
                        ...group,
                        categories: group.categories.map((category) => {
                            if (category.categoryId !== model.id) {
                                return category;
                            }

                            previousCategory = category;

                            return {
                                ...category,
                                name: model.name,
                                contentStatus: model.contentStatus
                            };
                        }).filter(category => category.contentStatus === SpintrTypes.ContentStatus.Published),
                    }),
            ));

            try {
                await updatePlannerCategoryAsync(model.id, {
                    name: model.name,
                    contentStatus: model.contentStatus,
                });
                return;
            } catch (err) { }

            if (!previousCategory) {
                return;
            }

            setData((prevData) => prevData.map(
                (group) => group.categories.every((category) => category.categoryId !== model.id)
                    ? group
                    : ({
                        ...group,
                        categories: group.categories.map((category) => {
                            if (category.categoryId !== model.id) {
                                return category;
                            }

                            return previousCategory;
                        }),
                    }),
            ));
        },
        [],
    );

    
    const onCategoryTypeUpdated = useCallback<UpdateCategoryTypeHandler>(
        async (model) => {
            let previousName: string = "";

            setData((prevData) => prevData.map(
                (group) => group.categories.every((category) => category.categoryTypeId !== model.id)
                    ? group
                    : ({
                        ...group,
                        categories: group.categories.map((category) => {
                            if (category.categoryTypeId !== model.id) {
                                return category;
                            }

                            previousName = category.name;

                            return {
                                ...category,
                                categoryTypeName: model.name,
                            };
                        }),
                    }),
            ));

            try {
                await updatePlannerCategoryTypeAsync(model.id, {
                    name: model.name,
                    contentStatus: model.contentStatus,
                });
                return;
            } catch (err) { }

            if (previousName.length === 0) {
                return;
            }

            setData((prevData) => prevData.map(
                (group) => group.categories.every((category) => category.categoryTypeId !== model.id)
                    ? group
                    : ({
                        ...group,
                        categories: group.categories.map((category) => {
                            if (category.categoryTypeId !== model.id) {
                                return category;
                            }

                            return {
                                ...category,
                                categoryTypeName: previousName
                            };
                        }),
                    }),
            ));
        },
        [],
    );

    useEffect(() => {
        dispatch(queryPlannerItems({
            after: new Date(timelineDuration.startMilliseconds),
            before: new Date(timelineDuration.endMilliseconds),
            status: SpintrTypes.ContentStatus.Published,
            boardId: boardId
        }));

        dispatch(queryPlannerBoards());
        dispatch(queryPlannerLabels());
        dispatch(queryPlannerCategories({take: 1000, status: SpintrTypes.ContentStatus.Draft}));

        api.get("/api/v1/plannersettings").then((response: AxiosResponse) => {
            setSettings(response.data);
        }).catch(() => {});
    }, [dispatch, boardId]);

    const todayStyle = useMemo<CSSProperties>(() => {
        const relativeToday = todayMs - timelineDuration.startMilliseconds;
        const percentage = relativeToday / timelineDuration.totalMilliseconds;

        return {
            left: `${timelineWidth * percentage}px`,
            top: state.headerHeight
        };
    }, [timelineDuration, timelineWidth, todayMs, state.headerHeight]);

    const positioning : PlannerPositioning = useMemo(() => {
        const result = {};

        for (const board of data) {
            const b = {
                height: plannerStyling.boardTitleRowHeight,
                nodes: {}
            }

            for (const category of board.categories) {
                const c = {
                    height: board.expanded ? plannerStyling.categoryRowHeight : 0
                }

                if (board.expanded) {
                    b.height += plannerStyling.categoryRowHeight;
                }

                for (const item of category.items) {
                    if (board.expanded && item.items.length > 1 && item.expanded) {
                        const normalOffset = (plannerStyling.categoryRowHeight - plannerStyling.smallBarHeight) / 2;
                        const additionalHeight = (item.items.length) * plannerStyling.smallBarHeight + normalOffset;

                        b.height += additionalHeight;
                        c.height += additionalHeight;
                    }
                }

                b.nodes[category.key] = c;
            }

            result[board.key] = b;
        }

        for (let i = 0; i < data.length; i++) {
            if (i === 0) {
                result[data[i].key].topOffset = state.headerHeight;
            } else {
                result[data[i].key].topOffset = result[data[i - 1].key].topOffset + result[data[i - 1].key].height;
            }

            for (let y = 0; y < data[i].categories.length; y++) {
                if (y === 0) {
                    result[data[i].key].nodes[data[i].categories[y].key].topOffset = plannerStyling.boardTitleRowHeight;
                } else {
                    result[data[i].key].nodes[data[i].categories[y].key].topOffset = result[data[i].key].nodes[data[i].categories[y - 1].key].topOffset + result[data[i].key].nodes[data[i].categories[y - 1].key].height;
                }
            }
        }

        return result;
    }, [data, state.headerHeight]);

    const totalWidth = columnDurations.length * columnWidth;
    const virtuosoComponents = useMemo(() => ({ Scroller: TimelineScroller }), []);

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            initialTopMostItemIndex: 0
        }));
    }, [timelineMode]);

    useEffect(() => {
        if (state.initialTopMostItemIndex !== 0) {
            return;
        }

        const totalWidth = window.document.querySelector(".timeline-right")?.clientWidth || 0;
        const index = getIndexFromDate(new Date());
        const totalVisibleItems = Math.floor(totalWidth / columnWidth);
        const initialTopMostItemIndex = index - Math.round(totalVisibleItems / 2);

        setState((prevState) => ({
            ...prevState,
            initialTopMostItemIndex
        }));
    }, [state.initialTopMostItemIndex]);

    const setHeaderHeight = useCallback((height: number) => {
        setState((prevState) => ({
            ...prevState,
            headerHeight: height
        }));
    }, [timelineMode, stateProps.labelLookup, state.headerHeight]);

    const onTimelineBoardsReportHeight = useCallback((height: number) => {
        setState((prevState) => ({
            ...prevState,
            timelineBoardsHeight: height
        }));
    }, [state.timelineBoardsHeight]);

    const calculatedVirtuosoHeight = useMemo(() => {
        //@ts-ignore
        return state.headerHeight + Object.values(positioning).map(x => x.height).reduce((total, num) => total + num, 0);
    }, [state.headerHeight, positioning]);

    return (
        <section className={[
            "Timeline",
            "timeline",
            state.axisMinimized ? "axis-minimized" : ""
        ].filter(x => x).join(" ")}>
            <div className="timeline-left">
                <TimelineBoards
                    headerHeight={state.headerHeight}
                    data={data}
                    positioning={positioning}
                    toggleBoardExpanded={toggleBoardExpanded}
                    displayBoardForm={displayBoardForm}
                    onCreateClick={onCreateClick}
                    reportHeight={onTimelineBoardsReportHeight}
                    isSmallViewMode={stateProps.isSmallViewMode}
                    isLoading={!(stateProps.initialBoardsFetchDone && stateProps.initialItemsFetchDone)}
                    onCategoryUpdated={onCategoryUpdated}
                    onCategoryTypeUpdated={onCategoryTypeUpdated}
                    rights={rights}
                    settings={settings}
                    boardId={boardId}
                />
            </div>
            <div className="Timeline-axisToggle-wrapper">
                <UnstyledButton
                    className={"Timeline-axisToggle box-shadow"}
                    onClick={onAxisToggleClicked}
                    style={{ top: state.headerHeight }}
                >
                    <Visage2Icon
                        icon={state.axisMinimized ? "arrow-right-1" : "arrow-left"}
                        size="small" />
                </UnstyledButton>
            </div>
            <div className="timeline-right">
                {!!state.initialTopMostItemIndex && (
                    <TimelineContext.Provider value={{
                        data,
                        timelineMode,
                        positioning,
                        totalWidth,
                        onBarClick,
                        onBarGroupClicked,
                        onItemDurationChange,
                        timelineDuration,
                        timelineWidth,
                        todayStyle,
                        headerHeight: state.headerHeight,
                        todayMs: state.todayMs,
                        columnDurations,
                        onCategoryUpdated,
                        onRailClicked,
                        onGroupClicked,
                        rights
                    }}>
                        <Virtuoso
                            className="virtuoso-item-list"
                            ref={virtuosoRef}
                            horizontalDirection
                            style={{
                                height: calculatedVirtuosoHeight
                            }}
                            initialTopMostItemIndex={state.initialTopMostItemIndex}
                            totalCount={columnDurations.length}
                            itemContent={(index) => (
                                <TimelineColumn
                                    key={index}
                                    item={columnDurations[index]}
                                    columnIndex={index}
                                    columnWidth={columnWidth}
                                    timelineMode={timelineMode}
                                    label={Object.values(stateProps.labelLookup).find(label =>
                                        label.formattedDate.getFullYear() === columnDurations[index].start.getFullYear() &&
                                        label.formattedDate.getMonth() === columnDurations[index].start.getMonth() &&
                                        label.formattedDate.getDate() === columnDurations[index].start.getDate()
                                    )}
                                    displayLabelRow={Object.keys(stateProps.labelLookup).length > 0}
                                    reportHeaderHeight={setHeaderHeight}
                                    rights={rights}
                                />
                            )}
                            components={virtuosoComponents}
                        />
                    </TimelineContext.Provider>
                )}
            </div>
        </section>
    )
}

export default Timeline;
